<template>
  <div class="workspace-shell" role="dialog" aria-modal="true" aria-label="本地工作区">
    <div class="workspace-backdrop" @click="$emit('close')"></div>
    <section class="workspace-panel">
      <header class="workspace-header">
        <div>
          <h2>本地工作区</h2>
          <p>{{ files.length }} 个文件 · {{ formatSize(totalSize) }}</p>
        </div>
        <button class="icon-button" type="button" title="关闭工作区" @click="$emit('close')">×</button>
      </header>

      <div class="workspace-intro">
        <strong>把文件放在这里，和 AI 按需协作</strong>
        <p>文件只保存在本地。AI 会先查看文件清单，需要时再读取指定内容，不会自动发送全文。</p>
      </div>

      <button
        class="drop-zone"
        :class="{ dragging: isDragging, busy: importing }"
        type="button"
        @click="$refs.fileInput.click()"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="handleDrop"
      >
        <span class="drop-icon">＋</span>
        <span class="drop-title">{{ importing ? '正在导入文件' : '添加到工作区' }}</span>
        <span class="drop-subtitle">文本、代码、CSV、XLSX、DOCX、PDF 或图片</span>
      </button>
      <input
        ref="fileInput"
        class="hidden-input"
        type="file"
        multiple
        accept="image/*,.txt,.md,.json,.csv,.xlsx,.docx,.pdf,.js,.ts,.vue,.css,.html,.py,.php,.java,.kt,.c,.cpp,.cs,.go,.rs,.sql,.log,.xml,.yaml,.yml"
        @change="handleFileInput"
      >

      <div v-if="notice" class="workspace-notice" :class="noticeType">{{ notice }}</div>

      <main class="workspace-list">
        <div v-if="loading" class="empty-state">正在读取工作区...</div>
        <div v-else-if="files.length === 0" class="empty-state">工作区为空</div>
        <article v-for="file in files" v-else :key="file.id" class="file-row">
          <div class="file-mark" :data-kind="file.kind">{{ fileMark(file) }}</div>
          <div class="file-main">
            <div class="file-title-row">
              <strong :title="file.name">{{ file.name }}</strong>
              <span v-if="file.pinned" class="scope-badge">全局</span>
              <span class="status-badge" :class="file.status">{{ statusLabel(file.status) }}</span>
            </div>
            <div class="file-meta">{{ formatSize(file.size) }}<span v-if="statsLabel(file)"> · {{ statsLabel(file) }}</span></div>
            <div v-if="file.capabilities && file.capabilities.length" class="file-capabilities">
              <span v-for="capability in visibleCapabilities(file)" :key="capability" class="capability-badge">{{ capabilityLabel(capability) }}</span>
              <span v-if="file.source === 'model'" class="capability-badge generated-badge">AI 生成 · v{{ file.version || 1 }}</span>
            </div>
            <p v-if="file.source === 'model'" class="file-origin">
              {{ file.operation === 'workspace_transform' ? '由工作区转换生成' : '由 AI 生成' }}<span v-if="file.sourceFileIds && file.sourceFileIds.length"> · 来源 {{ file.sourceFileIds.length }} 个工作区文件</span>
            </p>
            <p v-if="file.error" class="file-error">{{ file.error }}</p>
            <p v-else-if="file.preview" class="file-preview">{{ file.preview }}</p>
          </div>
          <div class="file-actions">
            <button type="button" title="预览" :disabled="file.status !== 'ready'" @click="openPreview(file)">预览</button>
            <button type="button" title="用于对话" :disabled="file.status !== 'ready'" @click="useFile(file)">用于对话</button>
            <button type="button" :title="file.pinned ? '移回当前会话' : '固定到全局'" @click="togglePinned(file)">{{ file.pinned ? '取消固定' : '固定' }}</button>
            <button type="button" title="下载" @click="downloadFile(file)">下载</button>
            <button v-if="file.status === 'failed'" type="button" title="重新解析" @click="retryFile(file)">重试</button>
            <button class="danger" type="button" title="删除" @click="removeFile(file)">删除</button>
          </div>
        </article>
      </main>
    </section>

    <div v-if="previewFile" class="preview-overlay" @click.self="closePreview">
      <section class="preview-dialog">
        <header>
          <div>
            <h3>{{ previewFile.name }}</h3>
            <p>{{ formatSize(previewFile.size) }}</p>
          </div>
          <button class="icon-button" type="button" title="关闭预览" @click="closePreview">×</button>
        </header>
        <img v-if="previewImageUrl" class="preview-image" :src="previewImageUrl" :alt="previewFile.name">
        <div v-else-if="previewRows.length" class="table-wrap">
          <table>
            <tbody>
              <tr v-for="(row, rowIndex) in previewRows" :key="rowIndex">
                <td v-for="(cell, cellIndex) in row" :key="cellIndex">{{ cell }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <pre v-else class="preview-text">{{ previewText }}</pre>
      </section>
    </div>
  </div>
</template>

<script>
import { workspaceService } from '@/utils/workspace';

export default {
  name: 'WorkspacePanel',
  props: {
    sessionId: { type: String, default: '' }
  },
  data() {
    return {
      files: [],
      loading: true,
      importing: false,
      isDragging: false,
      notice: '',
      noticeType: 'info',
      previewFile: null,
      previewText: '',
      previewRows: [],
      previewImageUrl: ''
    };
  },
  computed: {
    totalSize() {
      return this.files.reduce((total, file) => total + (file.size || 0), 0);
    }
  },
  watch: {
    sessionId() {
      this.loadFiles();
    }
  },
  mounted() {
    this.loadFiles();
    window.addEventListener('aihelp-workspace-changed', this.loadFiles);
  },
  beforeDestroy() {
    window.removeEventListener('aihelp-workspace-changed', this.loadFiles);
    this.closePreview();
  },
  methods: {
    async loadFiles() {
      this.loading = true;
      try {
        this.files = await workspaceService.listFiles(this.sessionId);
      } catch (error) {
        this.showNotice(error.message || '读取工作区失败', 'error');
      } finally {
        this.loading = false;
      }
    },
    showNotice(message, type = 'info') {
      this.notice = message;
      this.noticeType = type;
      clearTimeout(this.noticeTimer);
      this.noticeTimer = setTimeout(() => { this.notice = ''; }, 5000);
    },
    async importFiles(fileList) {
      const files = Array.from(fileList || []);
      if (!files.length || this.importing) return;
      this.importing = true;
      let imported = 0;
      const errors = [];
      for (const file of files) {
        try {
          await workspaceService.addFile(file, { sessionId: this.sessionId });
          imported += 1;
        } catch (error) {
          errors.push(`${file.name}: ${error.message || '导入失败'}`);
        }
      }
      this.importing = false;
      await this.loadFiles();
      if (errors.length) this.showNotice(errors.join('；'), 'error');
      else this.showNotice(`已导入 ${imported} 个文件`, 'success');
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },
    handleFileInput(event) {
      this.importFiles(event.target.files);
    },
    handleDrop(event) {
      this.isDragging = false;
      this.importFiles(event.dataTransfer.files);
    },
    useFile(file) {
      this.$emit('use-file', {
        id: file.id,
        name: file.name,
        kind: file.kind,
        reference: `请使用工作区文件《${file.name}》（file_id: ${file.id}）完成我的要求。`
      });
      this.showNotice(`已引用《${file.name}》`, 'success');
    },
    async togglePinned(file) {
      try {
        await workspaceService.setPinned(file.id, !file.pinned, this.sessionId);
      } catch (error) {
        this.showNotice(error.message || '更新失败', 'error');
      }
    },
    async removeFile(file) {
      if (!window.confirm(`删除工作区文件《${file.name}》？`)) return;
      try {
        await workspaceService.deleteFile(file.id);
      } catch (error) {
        this.showNotice(error.message || '删除失败', 'error');
      }
    },
    async retryFile(file) {
      try {
        await workspaceService.retryFile(file.id);
      } catch (error) {
        this.showNotice(error.message || '重新解析失败', 'error');
      }
    },
    async openPreview(file) {
      this.closePreview();
      this.previewFile = file;
      try {
        if (file.kind === 'image') {
          const blob = await workspaceService.getBlob(file.id);
          this.previewImageUrl = URL.createObjectURL(blob);
        } else if (file.kind === 'spreadsheet') {
          const indexes = await workspaceService.getIndexes(file.id);
          this.previewRows = indexes && indexes.sheets && indexes.sheets[0]
            ? indexes.sheets[0].rows.slice(0, 100).map(row => row.slice(0, 30))
            : [];
        } else {
          this.previewText = await workspaceService.readFile({ file_id: file.id, mode: file.stats && file.stats.pages ? 'pages' : 'lines', start: 0, limit: 300 });
        }
      } catch (error) {
        this.previewText = error.message || '预览失败';
      }
    },
    closePreview() {
      if (this.previewImageUrl) URL.revokeObjectURL(this.previewImageUrl);
      this.previewFile = null;
      this.previewText = '';
      this.previewRows = [];
      this.previewImageUrl = '';
    },
    async downloadFile(file) {
      try {
        await workspaceService.downloadFile(file.id);
      } catch (error) {
        this.showNotice(error.message || '下载失败', 'error');
      }
    },
    formatSize(bytes) {
      const value = Number(bytes) || 0;
      if (value < 1024) return `${value} B`;
      if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
      return `${(value / 1024 / 1024).toFixed(1)} MB`;
    },
    statusLabel(status) {
      return { parsing: '解析中', ready: '就绪', failed: '失败', queued: '等待中' }[status] || status;
    },
    statsLabel(file) {
      const stats = file.stats || {};
      if (stats.sheets) return `${stats.sheets} 个工作表 · ${stats.rows || 0} 行`;
      if (stats.pages) return `${stats.pages} 页`;
      if (stats.lines) return `${stats.lines} 行`;
      if (stats.width && stats.height) return `${stats.width} × ${stats.height}`;
      return '';
    },
    visibleCapabilities(file) {
      return (file.capabilities || []).filter(capability => ['inspect', 'search', 'read_range', 'read_lines', 'read_pages', 'eval', 'transform', 'extract'].includes(capability)).slice(0, 4);
    },
    capabilityLabel(capability) {
      return {
        inspect: '结构',
        search: '搜索',
        read_range: '范围读取',
        read_lines: '按行读取',
        read_pages: '按页读取',
        eval: '可分析',
        transform: '可转换',
        extract: '可提取'
      }[capability] || capability;
    },
    fileMark(file) {
      if (file.kind === 'image') return 'IMG';
      if (file.kind === 'spreadsheet') return 'XLS';
      if (file.extension === 'pdf') return 'PDF';
      if (file.extension === 'docx') return 'DOC';
      return (file.extension || 'TXT').slice(0, 4).toUpperCase();
    }
  }
};
</script>

<style scoped>
.workspace-shell { position: fixed; inset: 0; z-index: 2100; pointer-events: none; }
.workspace-backdrop { position: absolute; inset: 0; background: rgba(0, 0, 0, .28); pointer-events: auto; }
.workspace-panel { position: absolute; top: 0; right: 0; width: min(520px, 100vw); height: 100%; background: var(--bg-color, #fff); color: var(--text-color, #1f2937); box-shadow: -12px 0 36px rgba(0, 0, 0, .16); display: flex; flex-direction: column; pointer-events: auto; }
.workspace-header { min-height: 76px; padding: 16px 18px; border-bottom: 1px solid rgba(128, 128, 128, .18); display: flex; justify-content: space-between; align-items: center; }
.workspace-header h2, .preview-dialog h3 { font-size: 17px; line-height: 1.4; }
.workspace-header p, .preview-dialog header p { color: #6b7280; font-size: 12px; margin-top: 3px; }
.workspace-intro { margin: 14px 18px 0; padding: 11px 12px; border-left: 3px solid #4f46e5; background: rgba(79, 70, 229, .07); }
.workspace-intro strong { display: block; color: #3730a3; font-size: 12px; line-height: 1.45; }
.workspace-intro p { margin: 4px 0 0; color: #6b7280; font-size: 11px; line-height: 1.55; }
.icon-button { width: 36px; height: 36px; border: 0; background: transparent; color: inherit; font-size: 27px; line-height: 1; cursor: pointer; }
.drop-zone { margin: 16px 18px 10px; min-height: 116px; border: 1px dashed #9ca3af; background: rgba(59, 130, 246, .035); color: inherit; display: flex; flex-direction: column; align-items: center; justify-content: center; cursor: pointer; transition: border-color .18s, background .18s; }
.drop-zone.dragging { border-color: #2563eb; background: rgba(37, 99, 235, .1); }
.drop-zone.busy { cursor: wait; opacity: .72; }
.drop-icon { font-size: 24px; color: #2563eb; }
.drop-title { font-size: 14px; font-weight: 600; margin-top: 4px; }
.drop-subtitle { font-size: 12px; color: #6b7280; margin-top: 4px; }
.hidden-input { display: none; }
.workspace-notice { margin: 0 18px 8px; padding: 8px 10px; font-size: 12px; border-left: 3px solid #2563eb; background: rgba(37, 99, 235, .08); }
.workspace-notice.error { border-color: #dc2626; background: rgba(220, 38, 38, .08); }
.workspace-notice.success { border-color: #059669; background: rgba(5, 150, 105, .08); }
.workspace-list { overflow: auto; flex: 1; border-top: 1px solid rgba(128, 128, 128, .14); }
.empty-state { padding: 52px 20px; text-align: center; color: #6b7280; font-size: 13px; }
.file-row { padding: 14px 18px; border-bottom: 1px solid rgba(128, 128, 128, .14); display: grid; grid-template-columns: 46px minmax(0, 1fr); gap: 12px; }
.file-mark { width: 46px; height: 46px; border: 1px solid rgba(37, 99, 235, .25); background: rgba(37, 99, 235, .08); color: #1d4ed8; display: grid; place-items: center; font-size: 11px; font-weight: 700; }
.file-mark[data-kind="spreadsheet"] { color: #047857; border-color: rgba(5, 150, 105, .25); background: rgba(5, 150, 105, .08); }
.file-mark[data-kind="image"] { color: #b45309; border-color: rgba(217, 119, 6, .25); background: rgba(217, 119, 6, .08); }
.file-main { min-width: 0; }
.file-title-row { display: flex; align-items: center; gap: 6px; min-width: 0; }
.file-title-row strong { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-size: 14px; }
.scope-badge, .status-badge { flex: 0 0 auto; font-size: 10px; padding: 2px 5px; border: 1px solid rgba(128, 128, 128, .25); color: #6b7280; }
.status-badge.ready { color: #047857; border-color: rgba(5, 150, 105, .35); }
.status-badge.failed { color: #b91c1c; border-color: rgba(220, 38, 38, .35); }
.file-meta { color: #6b7280; font-size: 11px; margin-top: 4px; }
.file-capabilities { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 6px; }
.capability-badge { border: 1px solid rgba(79, 70, 229, .2); background: rgba(79, 70, 229, .06); color: #4f46e5; padding: 2px 5px; font-size: 10px; line-height: 1.2; }
.generated-badge { border-color: rgba(5, 150, 105, .25); background: rgba(5, 150, 105, .07); color: #047857; }
.file-origin { color: #6b7280; font-size: 11px; margin-top: 5px; }
.file-preview { color: #6b7280; font-size: 12px; margin-top: 6px; line-height: 1.45; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.file-error { color: #b91c1c; font-size: 12px; margin-top: 6px; }
.file-actions { grid-column: 2; display: flex; flex-wrap: wrap; gap: 6px; }
.file-actions button { border: 1px solid rgba(128, 128, 128, .25); background: transparent; color: inherit; padding: 5px 8px; font-size: 11px; cursor: pointer; }
.file-actions button:disabled { opacity: .45; cursor: default; }
.file-actions button.danger { color: #dc2626; }
.preview-overlay { position: fixed; inset: 0; z-index: 2200; background: rgba(0, 0, 0, .5); display: grid; place-items: center; padding: 20px; pointer-events: auto; }
.preview-dialog { width: min(920px, 100%); height: min(760px, 90vh); background: var(--bg-color, #fff); color: var(--text-color, #1f2937); display: flex; flex-direction: column; box-shadow: 0 20px 60px rgba(0, 0, 0, .28); }
.preview-dialog header { padding: 14px 18px; border-bottom: 1px solid rgba(128, 128, 128, .18); display: flex; justify-content: space-between; align-items: center; }
.preview-text { flex: 1; overflow: auto; padding: 18px; white-space: pre-wrap; overflow-wrap: anywhere; font: 13px/1.6 ui-monospace, SFMono-Regular, Consolas, monospace; }
.preview-image { width: 100%; height: calc(100% - 70px); object-fit: contain; background: #111; }
.table-wrap { flex: 1; overflow: auto; padding: 12px; }
.table-wrap table { border-collapse: collapse; font-size: 12px; }
.table-wrap td { border: 1px solid rgba(128, 128, 128, .22); padding: 6px 8px; white-space: nowrap; }
[data-theme="dark"] .workspace-panel, [data-theme="dark"] .preview-dialog { background: #18181b; color: #f4f4f5; }
[data-theme="dark"] .workspace-intro { background: rgba(99, 102, 241, .14); border-color: #818cf8; }
[data-theme="dark"] .workspace-intro strong { color: #c7d2fe; }
[data-theme="dark"] .capability-badge { border-color: rgba(129, 140, 248, .3); background: rgba(99, 102, 241, .16); color: #c7d2fe; }
[data-theme="dark"] .generated-badge { border-color: rgba(52, 211, 153, .3); background: rgba(5, 150, 105, .14); color: #6ee7b7; }
@media (max-width: 768px) {
  .workspace-backdrop { background: rgba(0, 0, 0, .42); }
  .workspace-panel { top: auto; bottom: 0; width: 100%; height: min(82vh, 760px); border-radius: 8px 8px 0 0; }
  .file-row { padding: 12px 14px; }
  .drop-zone { margin-left: 14px; margin-right: 14px; min-height: 100px; }
  .preview-overlay { padding: 0; }
  .preview-dialog { width: 100%; height: 100%; }
}
</style>
